import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { EnvBadge } from '@/deploy/components/ui/EnvBadge';
import { useSessionStore } from '@/deploy/stores/session.store';
import { useDeployEnvStore } from '@/deploy/stores/deployEnv.store';
import {
  resourcePacksService, type ResourcePack, type PackCreateInput, type DeployResultEntry,
} from '@/deploy/services/resource-packs.service';
import {
  behaviorPacksService, type BehaviorPack,
} from '@/deploy/services/behavior-packs.service';
import { cdnConfigService } from '@/deploy/services/cdn-config.service';
import type { EnvironmentId } from '@/deploy/types';
import { ENVIRONMENTS, ENVIRONMENT_BY_ID } from '@/deploy/types';
import { formatRelative } from '@/deploy/utils/format';
import styles from './ResourcePacks.module.scss';

type Tab = 'resource' | 'behavior';

export function ResourcePacks() {
  const project = useSessionStore((s) => s.currentProject());
  const [tab, setTab] = useState<Tab>('resource');
  const storeEnv = useDeployEnvStore((s) => s.env) as EnvironmentId;
  const setEnvId = useDeployEnvStore((s) => s.setEnv);

  // Un resource pack ne vit que sur les CDN configures du projet. Avec un seul CDN (le cas courant), pas de choix
  // d'environnement : la page et le deploiement visent ce CDN (prod si la config n'est pas lisible).
  const [cdnEnvs, setCdnEnvs] = useState<EnvironmentId[]>([]);
  useEffect(() => {
    if (!project.id) return;
    cdnConfigService.listAll(project.id)
      .then((all) => setCdnEnvs(ENVIRONMENTS.map((e) => e.id).filter((id) => all?.[id]?.enabled)))
      .catch(() => setCdnEnvs([]));
  }, [project.id]);
  const multiEnv = cdnEnvs.length > 1;
  const envId: EnvironmentId = multiEnv
    ? (cdnEnvs.includes(storeEnv) ? storeEnv : cdnEnvs[0])
    : (cdnEnvs[0] ?? 'prod');

  const [resourcePacks, setResourcePacks] = useState<ResourcePack[]>([]);
  const [behaviorPacks, setBehaviorPacks] = useState<BehaviorPack[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncInfo, setSyncInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [deployTarget, setDeployTarget] = useState<ResourcePack | null>(null);
  const [editTarget, setEditTarget] = useState<ResourcePack | BehaviorPack | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ kind: Tab; pack: ResourcePack | BehaviorPack } | null>(null);

  const reload = useCallback(async () => {
    if (!project.id) return;
    setLoading(true);
    setError(null);
    try {
      const [rp, bp] = await Promise.all([
        resourcePacksService.list(project.id),
        behaviorPacksService.list(project.id),
      ]);
      setResourcePacks(rp);
      setBehaviorPacks(bp);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { void reload(); }, [reload]);

  const syncAll = async () => {
    if (!project.id) return;
    setSyncing(true);
    setSyncInfo(null);
    try {
      const res = tab === 'resource'
        ? await resourcePacksService.syncAll(project.id)
        : await behaviorPacksService.syncAll(project.id);
      await reload();
      // Un sync muet ne permet pas de distinguer « rien de neuf » d'un quota GitHub épuisé.
      const errors = res?.errors ?? [];
      setSyncInfo(`${res?.ok ?? 0} pack(s) synchronisé(s)`
        + (res?.ko ? `, ${res.ko} en échec` : '')
        + (errors.length ? ` — ${errors.join(' ; ')}` : ''));
    } catch (e) {
      setSyncInfo(e instanceof Error ? e.message : 'Sync en échec');
    } finally {
      setSyncing(false);
    }
  };

  const syncOne = async (kind: Tab, id: string) => {
    if (!project.id) return;
    if (kind === 'resource') await resourcePacksService.sync(project.id, id);
    else await behaviorPacksService.sync(project.id, id);
    await reload();
  };

  const rpOutdated = resourcePacks.filter((p) => p.latestAvailable && p.latestAvailable !== p.currentVersion).length;
  const bpOutdated = behaviorPacks.filter((p) => p.latestAvailable && p.latestAvailable !== p.currentVersion).length;

  const envName = ENVIRONMENTS.find((e) => e.id === envId)?.name ?? envId;

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        {multiEnv ? (
        <nav className={styles.envNav}>
          {ENVIRONMENTS.filter((e) => cdnEnvs.includes(e.id)).map((e) => (
            <button
              key={e.id}
              type="button"
              className={[styles.envNavItem, e.id === envId && styles.envNavItemActive].filter(Boolean).join(' ')}
              onClick={() => setEnvId(e.id)}
            >
              <EnvBadge env={e.id} size="sm" />
              <span>{e.name}</span>
            </button>
          ))}
        </nav>
        ) : <span />}
        <div className={styles.headerActions}>
          <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={14} />}
            onClick={syncAll} loading={syncing}>
            Sync GitHub
          </Button>
          <Button variant="primary" size="sm" leftIcon={<Icon name="plus" size={14} />}
            onClick={() => setCreateOpen(true)}>
            Créer un {tab === 'resource' ? 'resource' : 'behavior'} pack
          </Button>
        </div>
      </div>

      {syncInfo && <p className={styles.syncInfo}>{syncInfo}</p>}

      <div className={styles.tabs}>
        <button
          className={[styles.tab, tab === 'resource' && styles.tabActive].filter(Boolean).join(' ')}
          onClick={() => setTab('resource')}
        >
          <Icon name="package" size={14} />
          Resource Packs
          <Badge tone={tab === 'resource' ? 'primary' : 'muted'}>{resourcePacks.length}</Badge>
          {rpOutdated > 0 && <Badge tone="warning">{rpOutdated} maj</Badge>}
        </button>
        <button
          className={[styles.tab, tab === 'behavior' && styles.tabActive].filter(Boolean).join(' ')}
          onClick={() => setTab('behavior')}
        >
          <Icon name="layers" size={14} />
          Behavior Packs
          <Badge tone={tab === 'behavior' ? 'primary' : 'muted'}>{behaviorPacks.length}</Badge>
          {bpOutdated > 0 && <Badge tone="warning">{bpOutdated} maj</Badge>}
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.panel}>
        <header className={styles.panelHeader}>
          <h2>
            <Icon name={tab === 'resource' ? 'package' : 'layers'} size={18} />
            {tab === 'resource' ? (multiEnv ? `Resource packs · ${envName}` : 'Resource packs (CDN)') : 'Behavior packs (tracking)'}
          </h2>
          <Badge tone="muted">{tab === 'resource' ? resourcePacks.length : behaviorPacks.length}</Badge>
        </header>
        <div className={styles.packRows}>
          {tab === 'resource' ? (
            loading && resourcePacks.length === 0
              ? <div className={styles.empty}>Chargement…</div>
              : <ResourcePackRows
                  packs={resourcePacks}
                  envId={envId}
                  onSync={(id) => syncOne('resource', id)}
                  onDeploy={setDeployTarget}
                  onEdit={setEditTarget}
                  onDelete={(p) => setDeleteTarget({ kind: 'resource', pack: p })}
                />
          ) : (
            loading && behaviorPacks.length === 0
              ? <div className={styles.empty}>Chargement…</div>
              : <BehaviorPackRows
                  packs={behaviorPacks}
                  onSync={(id) => syncOne('behavior', id)}
                  onEdit={setEditTarget}
                  onDelete={(p) => setDeleteTarget({ kind: 'behavior', pack: p })}
                />
          )}
        </div>
      </section>

      {createOpen && (
        <CreatePackModal
          kind={tab}
          projectId={project.id}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); void reload(); }}
        />
      )}

      {editTarget && (
        <EditPackModal
          kind={tab}
          projectId={project.id}
          pack={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); void reload(); }}
        />
      )}

      {deployTarget && (
        <DeployPackModal
          projectId={project.id}
          pack={deployTarget}
          defaultEnv={envId}
          envs={multiEnv ? cdnEnvs : [envId]}
          onClose={() => setDeployTarget(null)}
          onDeployed={() => { void reload(); }}
        />
      )}

      {deleteTarget && (
        <DeletePackModal
          target={deleteTarget}
          projectId={project.id}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); void reload(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// Resource Packs list
// ============================================================
function ResourcePackRows({
  packs, envId, onSync, onDeploy, onEdit, onDelete,
}: {
  packs: ResourcePack[];
  envId: EnvironmentId;
  onSync: (id: string) => void | Promise<void>;
  onDeploy: (p: ResourcePack) => void;
  onEdit: (p: ResourcePack) => void;
  onDelete: (p: ResourcePack) => void;
}) {
  if (packs.length === 0) {
    return <div className={styles.empty}>Aucun resource pack. Crée-en un pour commencer.</div>;
  }
  return (
    <>
      {packs.map((p) => {
        const deployed = p.deployedVersions?.[envId];
        const at = p.lastDeployedAt?.[envId];
        const outdated = Boolean(p.latestAvailable && deployed && deployed !== p.latestAvailable);
        return (
          <div key={p.id} className={styles.packRow}>
            <div className={styles.packMain}>
              <span className={styles.packChip} style={{ background: deployed ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
                <Icon name="package" size={12} />
              </span>
              <div className={styles.packInfo}>
                <div className={styles.packName}>
                  {p.name}
                  {deployed ? (
                    <>
                      <Badge tone="info">v{deployed}</Badge>
                      {outdated && <Badge tone="warning"><Icon name="arrow-right" size={9} /> v{p.latestAvailable}</Badge>}
                    </>
                  ) : (
                    <Badge tone="muted">Non déployé</Badge>
                  )}
                </div>
                <div className={styles.packMeta}>
                  <a href={`https://github.com/${p.repository}`} target="_blank" rel="noreferrer">{p.repository}</a>
                  {' · '}{p.fileName}
                  {p.sizeMb != null && <> · {p.sizeMb} MB</>}
                  {at && <> · déployé {formatRelative(at)}</>}
                </div>
              </div>
            </div>
            <div className={styles.packActions}>
              <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={11} />}
                onClick={() => void onSync(p.id)}>Sync</Button>
              <Button variant="ghost" size="sm" leftIcon={<Icon name="settings" size={11} />}
                onClick={() => onEdit(p)}>Éditer</Button>
              <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={11} />}
                onClick={() => onDelete(p)}>Supprimer</Button>
              <Button variant="primary" size="sm" leftIcon={<Icon name="rocket" size={11} />}
                onClick={() => onDeploy(p)}
                disabled={!p.latestAvailable && !p.currentVersion}>
                Déployer
              </Button>
            </div>
          </div>
        );
      })}
    </>
  );
}

// ============================================================
// Behavior Packs list (tracking only)
// ============================================================
function BehaviorPackRows({
  packs, onSync, onEdit, onDelete,
}: {
  packs: BehaviorPack[];
  onSync: (id: string) => void | Promise<void>;
  onEdit: (p: BehaviorPack) => void;
  onDelete: (p: BehaviorPack) => void;
}) {
  if (packs.length === 0) {
    return <div className={styles.empty}>Aucun behavior pack. Crée-en un pour commencer.</div>;
  }
  return (
    <>
      {packs.map((p) => {
        const outdated = Boolean(p.latestAvailable && p.latestAvailable !== p.currentVersion);
        const attached = p.attachedToContainers ?? [];
        return (
          <div key={p.id} className={styles.packRow}>
            <div className={styles.packMain}>
              <span className={styles.packChip}><Icon name="layers" size={12} /></span>
              <div className={styles.packInfo}>
                <div className={styles.packName}>
                  {p.name}
                  <Badge tone="muted">v{p.currentVersion ?? '—'}</Badge>
                  {outdated && <Badge tone="warning"><Icon name="arrow-right" size={9} /> v{p.latestAvailable}</Badge>}
                </div>
                <div className={styles.packMeta}>
                  <a href={`https://github.com/${p.repository}`} target="_blank" rel="noreferrer">{p.repository}</a>
                  {' · '}{p.fileName}
                  {attached.length > 0
                    ? <> · embarqué dans {attached.join(', ')}</>
                    : <> · aucun container ne l'embarque</>}
                </div>
              </div>
            </div>
            <div className={styles.packActions}>
              <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={11} />}
                onClick={() => void onSync(p.id)}>Sync</Button>
              <Button variant="ghost" size="sm" leftIcon={<Icon name="settings" size={11} />}
                onClick={() => onEdit(p)}>Éditer</Button>
              <Button variant="ghost" size="sm" leftIcon={<Icon name="x" size={11} />}
                onClick={() => onDelete(p)}>Supprimer</Button>
            </div>
          </div>
        );
      })}
    </>
  );
}

// ============================================================
// Modals
// ============================================================
function CreatePackModal({
  kind, projectId, onClose, onCreated,
}: {
  kind: Tab;
  projectId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [fileName, setFileName] = useState('');
  const [repository, setRepository] = useState('');
  const [description, setDescription] = useState('');
  const [sizeMb, setSizeMb] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const valid = !!name.trim() && !!fileName.trim() && !!repository.trim();

  const submit = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      const input: PackCreateInput = {
        name: name.trim(),
        fileName: fileName.trim(),
        repository: repository.trim(),
        description: description.trim() || undefined,
        sizeMb: sizeMb ? Number(sizeMb) : undefined,
      };
      if (kind === 'resource') await resourcePacksService.create(projectId, input);
      else await behaviorPacksService.create(projectId, input);
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={kind === 'resource' ? 'Créer un resource pack' : 'Créer un behavior pack'}
      subtitle="Le sync GitHub se fera juste après la création"
      width="md"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button variant="primary" onClick={submit} disabled={!valid || submitting} loading={submitting}>
          Créer
        </Button>
      </>}
    >
      <div className={styles.form}>
        {err && <div className={styles.error}>{err}</div>}
        <label className={styles.field}>
          <span>Nom <em>requis</em></span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Pala" />
        </label>
        <label className={styles.field}>
          <span>Nom du fichier ZIP <em>requis</em></span>
          <input value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="PalaResourcePack.zip" />
        </label>
        <label className={styles.field}>
          <span>Repository GitHub <em>requis · org/nom</em></span>
          <input value={repository} onChange={(e) => setRepository(e.target.value)} placeholder="Paladium-Bedrock/PalaResourcePack" />
        </label>
        <label className={styles.field}>
          <span>Description</span>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className={styles.field}>
          <span>Taille (MB, indicatif)</span>
          <input type="number" step="0.1" value={sizeMb} onChange={(e) => setSizeMb(e.target.value)} />
        </label>
      </div>
    </Modal>
  );
}

function EditPackModal({
  kind, projectId, pack, onClose, onSaved,
}: {
  kind: Tab;
  projectId: string;
  pack: ResourcePack | BehaviorPack;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(pack.name);
  const [fileName, setFileName] = useState(pack.fileName);
  const [repository, setRepository] = useState(pack.repository);
  const [description, setDescription] = useState(pack.description ?? '');
  const [sizeMb, setSizeMb] = useState(pack.sizeMb != null ? String(pack.sizeMb) : '');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      const patch = {
        name: name.trim(),
        fileName: fileName.trim(),
        repository: repository.trim(),
        description: description.trim(),
        sizeMb: sizeMb ? Number(sizeMb) : undefined,
      };
      if (kind === 'resource') await resourcePacksService.update(projectId, pack.id, patch);
      else await behaviorPacksService.update(projectId, pack.id, patch);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Éditer ${pack.name}`}
      width="md"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button variant="primary" onClick={submit} disabled={submitting} loading={submitting}>Sauvegarder</Button>
      </>}
    >
      <div className={styles.form}>
        {err && <div className={styles.error}>{err}</div>}
        <label className={styles.field}>
          <span>Nom</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className={styles.field}>
          <span>Nom du fichier ZIP</span>
          <input value={fileName} onChange={(e) => setFileName(e.target.value)} />
        </label>
        <label className={styles.field}>
          <span>Repository</span>
          <input value={repository} onChange={(e) => setRepository(e.target.value)} />
        </label>
        <label className={styles.field}>
          <span>Description</span>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className={styles.field}>
          <span>Taille (MB)</span>
          <input type="number" step="0.1" value={sizeMb} onChange={(e) => setSizeMb(e.target.value)} />
        </label>
      </div>
    </Modal>
  );
}

function DeployPackModal({
  projectId, pack, defaultEnv, envs, onClose, onDeployed,
}: {
  projectId: string;
  pack: ResourcePack;
  defaultEnv: EnvironmentId;
  envs: EnvironmentId[];
  onClose: () => void;
  onDeployed: () => void;
}) {
  // Coche d'office l'environnement affiche : deployer depuis l'onglet Prod vise la prod.
  const [environments, setEnvironments] = useState<Set<EnvironmentId>>(new Set([defaultEnv]));
  const [version, setVersion] = useState(pack.latestAvailable ?? pack.currentVersion ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<DeployResultEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const toggleEnv = (e: EnvironmentId) => setEnvironments((s) => {
    const n = new Set(s);
    if (n.has(e)) n.delete(e); else n.add(e);
    return n;
  });

  const valid = environments.size > 0 && !!version.trim();

  const deploy = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      const res = await resourcePacksService.deploy(projectId, pack.id, {
        environments: Array.from(environments),
        version: version.trim(),
      });
      // La fenetre reste ouverte sur le resultat : se fermer d'elle-meme laissait croire que rien ne s'etait passe.
      setResults(res.results);
      if (res.results.some((r) => r.ok)) onDeployed();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Déployer ${pack.name}`}
      subtitle={`Upload SFTP sur le CDN du projet · ${pack.fileName}`}
      width="md"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>{results ? 'Fermer' : 'Annuler'}</Button>
        {!results && (
          <Button variant="primary" onClick={deploy} disabled={!valid || submitting} loading={submitting}>
            Déployer sur CDN
          </Button>
        )}
      </>}
    >
      <div className={styles.form}>
        {err && <div className={styles.error}>{err}</div>}
        {!results && (<>
          <label className={styles.field}>
            <span>Version à déployer</span>
            <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="0.0.43" />
          </label>
          {envs.length > 1 && (
          <div className={styles.field}>
            <span>Environnements cibles ({environments.size})</span>
            <div className={styles.envCheckRow}>
              {ENVIRONMENTS.filter((env) => envs.includes(env.id)).map((env) => (
                <label key={env.id} className={[
                  styles.envCheck,
                  environments.has(env.id) && styles.envCheckActive,
                ].filter(Boolean).join(' ')}>
                  <input
                    type="checkbox"
                    checked={environments.has(env.id)}
                    onChange={() => toggleEnv(env.id)}
                  />
                  <EnvBadge env={env.id} size="sm" />
                </label>
              ))}
            </div>
          </div>
          )}
          <div className={styles.info}>
            <Icon name="rocket" size={12} />
            <span>
              Télécharge l'asset <code>{pack.fileName}</code> depuis la release GitHub <code>v{version}</code>,
              le pousse via SFTP sur le CDN, puis exécute la commande de cache-clear configurée.
            </span>
          </div>
        </>)}
        {results && results.length > 0 && results.every((r) => r.ok) && (
          <div className={styles.info}>
            <Icon name="check" size={12} />
            <span>
              {envs.length > 1 ? (<>
                v{version} déployée. La liste affiche la version de l'onglet choisi
                ({ENVIRONMENT_BY_ID[defaultEnv]?.name ?? defaultEnv}) : change d'onglet pour voir les autres environnements.
              </>) : <>v{version} déployée sur le CDN.</>}
            </span>
          </div>
        )}
        {results && (
          <div className={styles.results}>
            {results.map((r) => {
              const tone = r.ok ? styles.resultOk : r.skipped ? styles.resultSkipped : styles.resultKo;
              return (
                <div key={r.environment} className={[styles.resultRow, tone].join(' ')}>
                  <Icon name={r.ok ? 'check' : r.skipped ? 'warn' : 'x'} size={14} />
                  <strong>{envs.length > 1 ? r.environment : 'CDN'}</strong>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {r.ok ? (
                      <code className={styles.resultPath}>{r.targetPath}</code>
                    ) : r.skipped ? (
                      <span className={styles.resultErr}>SKIP — {r.error}</span>
                    ) : (
                      <span className={styles.resultErr}>{r.error}</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

function DeletePackModal({
  target, projectId, onClose, onDeleted,
}: {
  target: { kind: Tab; pack: ResourcePack | BehaviorPack };
  projectId: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      if (target.kind === 'resource') await resourcePacksService.remove(projectId, target.pack.id);
      else await behaviorPacksService.remove(projectId, target.pack.id);
      onDeleted();
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal
      open onClose={onClose}
      title="Supprimer le pack"
      subtitle={target.pack.name}
      width="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button variant="danger" onClick={submit} disabled={submitting} loading={submitting}>Supprimer</Button>
      </>}
    >
      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
        La suppression de <strong>{target.pack.name}</strong> retire le pack du tracking.
        Les fichiers sur le CDN ne sont pas supprimés.
      </p>
    </Modal>
  );
}

// useMemo unused in some paths — re-export to avoid TS6133
void useMemo;
